import Keycloak from 'keycloak-js';
import * as Sentry from '@sentry/react';

export const keycloak = new Keycloak({
  url: import.meta.env.VITE_KEYCLOAK_URL ?? 'http://localhost:8080',
  realm: import.meta.env.VITE_KEYCLOAK_REALM ?? 'entity-management',
  clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID ?? 'entity-web',
});

const UPDATE_TOKEN_TIMEOUT_MS = 8_000;

/**
 * EW-FIX-95 — `keycloak.updateToken()` has no timeout of its own: if the refresh
 * request (or keycloak-js's internal callback queue, which every concurrent caller
 * — the background interval below AND graphql_client's requestMiddleware — shares)
 * never settles, the returned promise never resolves OR rejects. Both existing
 * `.catch()` handlers here and in graphql_client.ts were written assuming a REJECT
 * is the only failure shape; a HANG instead means neither ever fires, so nothing
 * logs and the caller (e.g. a TanStack Query poll's fetchFn) is stuck forever with
 * no new network request and no visible error — found live via an entity-import
 * wizard poll that silently stopped advancing right after a background token
 * refresh, with no console/Sentry trace. Racing a timeout turns the hang into the
 * same loud, logged rejection this file already treats a genuine refresh failure
 * as, continuing the "fail loud instead of silent hang" fix this file already
 * applies to the reject case.
 */
export function updateTokenOrTimeout(minValiditySeconds: number): Promise<boolean> {
  return new Promise<boolean>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(
        new Error(
          `keycloak.updateToken(${minValiditySeconds}) did not settle within ${UPDATE_TOKEN_TIMEOUT_MS}ms`,
        ),
      );
    }, UPDATE_TOKEN_TIMEOUT_MS);
    keycloak.updateToken(minValiditySeconds).then(
      (refreshed) => {
        clearTimeout(timer);
        resolve(refreshed);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

export async function initKeycloak(): Promise<boolean> {
  const authenticated = await keycloak.init({
    onLoad: 'login-required',
    checkLoginIframe: false,
    pkceMethod: 'S256',
  });

  // Auto-refresh token before expiry.
  // EW-FIX — same silent-failure shape as graphql_client.ts's requestMiddleware:
  // a failed refresh used to redirect with zero logging. Logged loudly here too so
  // a background-timer failure (as opposed to a request-triggered one) is equally
  // diagnosable from Sentry/console rather than only observable as "session died".
  setInterval(() => {
    updateTokenOrTimeout(30).catch((err) => {
      console.error('[Keycloak] background updateToken(30) failed:', err);
      Sentry.captureException(err, { tags: { context: 'keycloak_update_token_interval' } });
      keycloak.login().catch((loginErr) => {
        console.error('[Keycloak] background login() redirect itself failed:', loginErr);
        Sentry.captureException(loginErr, {
          tags: { context: 'keycloak_login_redirect_interval' },
        });
      });
    });
  }, 10_000);

  return authenticated;
}
